/**
 * Language Utility Functions
 * 语言工具函数
 *
 * Provides helper functions for multilingual support with fallback mechanism.
 * 提供多语言支持的辅助函数，带有回退机制。
 */

import store from '@/store'

/**
 * Get localized field from product (with default language fallback)
 * 获取产品的本地化字段（带默认语言回退）
 *
 * @param {Object} product - Product object / 产品对象
 * @param {string} field - Field name ('name', 'description', 'image') / 字段名
 * @returns {string} - Localized value, falls back to default if current language is empty / 本地化后的值
 */
export function getLocalizedField(product, field) {
  if (!product) return ''

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  // If using default language, return product's default field directly
  // 如果是默认语言，直接返回产品的默认字段
  if (currentLang === defaultLang) {
    return product[field] || ''
  }

  // Try to get translation for current language
  // 尝试获取当前语言的翻译
  const localizedValue = product?.languages?.[currentLang]?.[field]

  // If current language has non-empty value, return it; otherwise fallback to default
  // 如果当前语言有值且非空，返回该值；否则回退到默认值
  if (localizedValue && String(localizedValue).trim() !== '') {
    return localizedValue
  }

  // Fallback to product's default field (English)
  // 回退到产品的默认字段（英语）
  return product[field] || ''
}

/**
 * Get localized product object (with default language fallback)
 * 获取本地化的产品对象（带默认语言回退）
 *
 * @param {Object} product - Product object / 产品对象
 * @returns {Object} - Product object with localized fields / 包含本地化字段的产品对象
 */
export function getLocalizedProduct(product) {
  if (!product) return product

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  // If using default language, return original product directly
  // 如果是默认语言，直接返回原产品
  if (currentLang === defaultLang) {
    return product
  }

  const localized = product?.languages?.[currentLang]

  // If no translation for current language, return original product
  // 如果没有当前语言的翻译，返回原产品
  if (!localized) {
    return product
  }

  // Merge localized data, check each field individually for fallback
  // 合并本地化数据，每个字段单独检查是否需要回退
  return {
    ...product,
    name: localized.name && String(localized.name).trim() !== '' ? localized.name : product.name,
    description:
      localized.description && String(localized.description).trim() !== ''
        ? localized.description
        : product.description,
    image: localized.image && String(localized.image).trim() !== '' ? localized.image : product.image,
  }
}

/**
 * Get localized product group name (with default language fallback)
 * 获取产品分组的本地化名称（带默认语言回退）
 *
 * @param {Object} productGroup - Product group object / 产品分组对象
 * @returns {string} - Localized group name / 本地化后的分组名称
 */
export function getLocalizedGroupName(productGroup) {
  return getLocalizedField(productGroup, 'name')
}

/**
 * Get localized modification name (with default language fallback)
 * 获取修改项的本地化名称（带默认语言回退）
 *
 * API 的 languages 中可能使用 name、displayName 或 title 作为翻译字段。
 *
 * @param {Object} modification - Modification object / 修改项对象
 * @returns {string} - Localized modification name / 本地化后的修改项名称
 */
export function getLocalizedModificationName(modification) {
  if (!modification) return ''

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  // If not default language, try to get from languages object
  // 非默认语言时，尝试从 languages 对象中获取
  if (currentLang !== defaultLang) {
    const langData = modification?.languages?.[currentLang]
    if (langData) {
      const value = langData.name || langData.displayName || langData.title
      if (value && String(value).trim() !== '') {
        return value
      }
    }
  }

  // Fallback: use root-level fields
  // 回退：使用根级字段
  return modification?.name || modification?.displayName || modification?.title || ''
}

/**
 * Get localized modification option name (with default language fallback)
 * 获取修改项选项的本地化名称（带默认语言回退）
 *
 * @param {Object} option - Modification option object / 修改项选项对象
 * @returns {string} - Localized option name / 本地化后的选项名称
 */
export function getLocalizedOptionName(option) {
  if (!option) return ''

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  if (currentLang !== defaultLang) {
    const langData = option?.languages?.[currentLang]
    if (langData) {
      const value = langData.name || langData.displayName
      if (value && String(value).trim() !== '') {
        return value
      }
    }
  }

  return option?.name || option?.displayName || ''
}

/**
 * Get localized field from campaign data (with default language fallback)
 * 获取 campaign 的本地化字段（带默认语言回退）
 *
 * Campaign data uses `settings.languageSettings[langCode]` for translations.
 * campaign 数据使用 `settings.languageSettings[langCode]` 存储翻译。
 *
 * @param {Object} campaignData - Campaign data object / campaign 数据对象
 * @param {string} field - Field name ('description', 'logo', 'image', 'logoTitle', 'logoTitleLong', 'optinText', 'optinTitle', 'thankyouText', 'rejectText', 'acceptText') / 字段名
 * @returns {string} - Localized value, falls back to default if current language is empty / 本地化后的值
 */
export function getLocalizedCampaignField(campaignData, field) {
  if (!campaignData) return ''

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  // If using default language, return campaign's default field directly
  // 如果是默认语言，直接返回 campaign 的默认字段
  if (currentLang === defaultLang) {
    // For nested fields like rejectText/acceptText that live in settings
    // 对于 settings 下的嵌套字段（如 rejectText/acceptText）
    if (field === 'rejectText' || field === 'acceptText') {
      return campaignData?.settings?.[field] ?? ''
    }
    return campaignData[field] ?? ''
  }

  // Try to get translation for current language from settings.languageSettings or languageSettings
  // 尝试从 settings.languageSettings 或 languageSettings 获取当前语言的翻译
  const localizedValue = campaignData?.settings?.languageSettings?.[currentLang]?.[field] ??
    campaignData?.languageSettings?.[currentLang]?.[field]

  // If current language has non-empty value, return it
  // 如果当前语言有值且非空，返回该值
  if (localizedValue != null && localizedValue !== false && String(localizedValue).trim() !== '') {
    return localizedValue
  }

  // Fallback to campaign's default field
  // 回退到 campaign 的默认字段
  if (field === 'rejectText' || field === 'acceptText') {
    return campaignData?.settings?.[field] ?? ''
  }
  return campaignData[field] ?? ''
}

/**
 * Get localized message from customMessageSettings entry
 * 从 customMessageSettings 条目中获取本地化消息
 *
 * The data structure has a `message` field for the default language,
 * and a `languages` object keyed by language code, each with a `message` field.
 * 数据结构中有一个 `message` 字段作为默认语言，
 * 以及一个 `languages` 对象，按语言代码索引，每个包含 `message` 字段。
 *
 * @param {Object} messageEntry - e.g. customMessageSettings.popup.table or confirmation.table
 * @param {string} [field='message'] - The field to read / 要读取的字段
 * @returns {string} - Localized message / 本地化消息
 */
export function getLocalizedCustomMessage(messageEntry, field = 'message') {
  if (!messageEntry) return ''

  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'

  // If using default language, return the default field directly
  // 如果是默认语言，直接返回默认字段
  if (currentLang === defaultLang) {
    return messageEntry[field] ?? ''
  }

  // Try to get translation for current language from languages object
  // 尝试从 languages 对象获取当前语言的翻译
  const localizedValue = messageEntry?.languages?.[currentLang]?.[field]

  // If current language has non-empty value, return it
  // 如果当前语言有值且非空，返回该值
  if (localizedValue != null && String(localizedValue).trim() !== '') {
    return localizedValue
  }

  // Fallback to the default field
  // 回退到默认字段
  return messageEntry[field] ?? ''
}

/**
 * Convenience function to get UI translation text
 * 获取 UI 翻译文本的便捷函数
 *
 * @param {string} key - Translation key / 翻译 key
 * @param {string} fallback - Fallback value / 回退值
 * @returns {string} - Translated text / 翻译后的文本
 */
export function t(key, fallback) {
  if (!store.getters['language/t']) {
    return fallback || key
  }
  return store.getters['language/t'](key, fallback)
}

/**
 * Get UI translation text with parameter replacement
 * 获取带参数替换的 UI 翻译文本
 *
 * @param {string} key - Translation key / 翻译 key
 * @param {string} fallback - Fallback value / 回退值
 * @param {Object} params - Parameters to replace in the text / 要替换的参数
 * @returns {string} - Translated text with parameters replaced / 替换参数后的翻译文本
 *
 * @example
 * // Translation: "Select {n} (Required)"
 * tParams('select_n_required', 'Select {n} (Required)', { n: 3 })
 * // Returns: "Select 3 (Required)"
 *
 * @example
 * // Translation: "Select between {n} and {m}"
 * tParams('select_between_n_m', 'Select between {n} and {m}', { n: 1, m: 3 })
 * // Returns: "Select between 1 and 3"
 */
export function tParams(key, fallback, params = {}) {
  let text = t(key, fallback)
  // Replace all {key} placeholders with corresponding values
  // 替换所有 {key} 占位符为对应的值
  Object.keys(params).forEach(paramKey => {
    text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), params[paramKey])
  })
  return text
}

/**
 * Get current language code
 * 获取当前语言代码
 *
 * @returns {string} - Current language code / 当前语言代码
 */
export function getCurrentLanguage() {
  return store.state.language?.currentLanguage || 'en'
}

/**
 * Get default language code
 * 获取默认语言代码
 *
 * @returns {string} - Default language code / 默认语言代码
 */
export function getDefaultLanguage() {
  return store.state.language?.defaultLanguage || 'en'
}

/**
 * Check if current language is the default language
 * 检查当前是否为默认语言
 *
 * @returns {boolean}
 */
export function isDefaultLanguage() {
  const currentLang = store.state.language?.currentLanguage || 'en'
  const defaultLang = store.state.language?.defaultLanguage || 'en'
  return currentLang === defaultLang
}

/**
 * Get dayjs locale code from current language
 * 根据当前语言获取 dayjs 的 locale 代码
 *
 * Maps app language codes to dayjs locale identifiers.
 * 将应用语言代码映射为 dayjs locale 标识符。
 *
 * @returns {string} - dayjs locale code / dayjs 区域代码
 */
const LANG_TO_DAYJS_LOCALE = {
  en: 'en',
  zh: 'zh-cn',
  fr: 'fr',
}

export function getDayjsLocale() {
  const currentLang = store.state.language?.currentLanguage || 'en'
  return LANG_TO_DAYJS_LOCALE[currentLang] || 'en'
}

/**
 * Get list of supported languages
 * 获取支持的语言列表
 *
 * @returns {Array} - Supported languages list / 支持的语言列表
 */
export function getSupportedLanguages() {
  return store.state.language?.supportedLanguages || []
}

/**
 * Check if multiple languages are supported
 * 检查是否有多语言支持
 *
 * @returns {boolean}
 */
export function hasMultipleLanguages() {
  return store.getters['language/hasMultipleLanguages'] || false
}

/**
 * Change current language
 * 切换语言
 *
 * @param {string} langCode - Language code / 语言代码
 */
export function changeLanguage(langCode) {
  store.dispatch('language/changeLanguage', langCode)
}

/**
 * Reset language to default
 * 重置语言到默认值
 */
export function resetLanguage() {
  store.commit('language/resetLanguage')
}
